import { InfoCircleOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, Tooltip } from 'antd';
import { endOfDay, format, startOfDay, startOfYear } from 'date-fns';
import { FormProvider, useForm } from 'react-hook-form';

import { useExpelEnrolledSectionParticipantMutation } from '@/api/endpoints/schools/schoolSelectionPanel';
import type {
  TEnrolledSectionParticipant,
  IFormData,
} from '@/api/endpoints/schools/schoolSelectionPanel/enrolled/types';
import { EXPULSION_PARTICIPANT_VALIDATION_SCHEMA } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/EnrolledTab/(pages)/EnrolledListPage/consts';
import { DataSection, DatePickerField, TextAreaField } from '@/shared/components';
import { DATE_FORMATTERS } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import styles from './ExpulsionModal.module.scss';

interface IProp {
  schoolId: number;
  itemToOperate: TEnrolledSectionParticipant;
  isModalOpen: boolean;
  handleCancel: VoidFunction;
}

export const ExpulsionModal = ({ schoolId, itemToOperate, isModalOpen, handleCancel }: IProp) => {
  const sendApiRequest = useApiRequest();

  const [expelParticipant, { isLoading: isParticipantExpelling }] = useExpelEnrolledSectionParticipantMutation();

  const getDefaultValues = (): IFormData => ({
    expulsionReason: '',
    expelledAt: startOfDay(new Date()),
  });

  const methods = useForm<IFormData>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
    resolver: yupResolver(EXPULSION_PARTICIPANT_VALIDATION_SCHEMA),
  });

  const { id, name } = itemToOperate;

  const { handleSubmit, reset } = methods;

  const closeModal = () => {
    reset(getDefaultValues());
    handleCancel();
  };

  const expelParticipantHandler = handleSubmit(async ({ expulsionReason, expelledAt }) => {
    await sendApiRequest({
      successMessage: 'Студент отчислен',
      apiRequestCallback: () =>
        expelParticipant({
          schoolId,
          participantId: id,
          expulsionReason,
          expelledAt: format(expelledAt, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY),
        }),
      successCallback: closeModal,
    });
  });

  return (
    <Modal
      width={500}
      open={isModalOpen}
      onCancel={closeModal}
      footer={null}
      centered
    >
      <FormProvider {...methods}>
        <div className={styles.wrapper}>
          <p className={styles.title}>Отчислить студента</p>
          <p className={styles.description}>Укажите причину и дату отчисления студента: {name}</p>
          <DataSection
            titleText="Причина отчисления"
            isTitleRequired
            titlePostfix={
              <Tooltip
                styles={{ root: { maxWidth: 250 } }}
                title="Эта причина будет сохранена в карточке студента"
              >
                <InfoCircleOutlined />
              </Tooltip>
            }
          >
            <TextAreaField
              fieldName="expulsionReason"
              placeholder="Введите причину отчисления"
              autoSize={{ minRows: 2, maxRows: 6 }}
            />
          </DataSection>
          <DataSection
            titleText="Дата отчисления"
            isTitleRequired
          >
            <DatePickerField
              fieldName="expelledAt"
              format={{ format: DATE_FORMATTERS.DAY_MONTH_FULL_YEAR_MASK, type: 'mask' }}
              placeholder="дд.мм.гггг"
              minDate={startOfYear(new Date())}
              maxDate={endOfDay(new Date())}
              placement="topRight"
              styles={{ root: { maxWidth: 200 } }}
            />
          </DataSection>
          <div className={styles['buttons-wrapper']}>
            <Button onClick={closeModal}>Отмена</Button>
            <Button
              variant="solid"
              color="red"
              onClick={expelParticipantHandler}
              disabled={isParticipantExpelling}
            >
              Отчислить студента
            </Button>
          </div>
        </div>
      </FormProvider>
    </Modal>
  );
};
